/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Modal, ThemeProvider, getThemeByName, Box } from '@tishman/components';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { GenericErrorMessages } from '../../../buy-tickets/constants/constants';

import { ModalCard } from './ModalCard';
import { ModalErrorCard } from './ModalErrorCard';
import { TicketsErrorModal } from './TicketsErrorModal';

import type { BuyTicketsVariantPageData } from '../types';
import type { ShopCartSelection } from '../../../store/types';

const BuyTicketsModalContext = createContext<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setValue: (value: Error | undefined) => void;
  value: Error | undefined;
} | null>(null);

export const BuyTicketsModalProvider = BuyTicketsModalContext.Provider;

export const useBuyTicketsModal = () => {
  const [value, setValue] = useState<Error | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (value) setIsOpen(true);
  }, [value]);

  const modal = useContext(BuyTicketsModalContext);
  if (modal) return modal;

  return {
    isOpen,
    setIsOpen,
    setValue,
    value,
  };
};

interface BuyTicketsModalProps {
  data?: BuyTicketsVariantPageData | { page: { checkoutModalCard: boolean } };
  error?: Error;
  isOpen: boolean;
  onClose: () => void;
  onCloseFinished?: () => void;
  shopCartSelections: ShopCartSelection[];
  errorBlock?: JSX.Element;
}

interface ModalData {
  title?: string;
  description?: string;
  link?: {
    label: string;
    url: string;
  };
  subTitle?: string;
}

export const BuyTicketsModal = ({
  data,
  error,
  errorBlock,
  isOpen,
  onClose,
  onCloseFinished,
  shopCartSelections,
}: BuyTicketsModalProps) => {
  const ticketCount = shopCartSelections.reduce(
    (sum, item) => sum + (item?.count || 0),
    0,
  );
  const modalData =
    data?.page && 'modalCard' in data.page ? data.page.modalCard : null;

  const modalData2 =
    data?.page && 'modalCard2' in data.page ? data.page.modalCard2 : null;

  const modalData3 =
    data?.page && 'modalCard3' in data.page
      ? (data.page.modalCard3 as ModalData)
      : null;

  const modalData4 =
    data?.page && 'checkoutModalCard' in data.page
      ? data.page.checkoutModalCard
      : null;

  // Can't show a modal without data or an error
  if (!error || (!modalData && !modalData2 && !modalData3 && !modalData4))
    return null;

  let genericError;

  if (error?.message) {
    genericError = GenericErrorMessages.find(
      (err) => err.code === parseInt(error?.message.replace(/\D/g, '') ?? ''),
    );
  }

  const errorMessageTitle = genericError
    ? `${genericError?.title}`
    : error
    ? error?.name
    : '';

  const errorDescription = genericError
    ? `${genericError?.message}`
    : error
    ? error?.message
    : '';

  const modalMarkup = (() => {
    switch (error?.message) {
      case 'CARD':
        return modalData ? (
          <ModalCard
            closeModal={onClose}
            {...modalData}
            description={modalData.description || undefined}
            link={modalData.link as { label: string; url: string } | undefined}
            subTitle={modalData.subTitle || undefined}
            ticketCount={ticketCount}
            title={modalData.title || undefined}
          />
        ) : null;
      case 'UPSELL':
        return modalData2 ? (
          <ModalCard
            closeModal={onClose}
            {...modalData2}
            description={modalData2.description || undefined}
            ticketCount={ticketCount}
            title={modalData2.title || undefined}
          />
        ) : null;
      case 'ANYTIME':
        return modalData3 ? (
          <ModalCard
            closeModal={onClose}
            {...modalData3}
            description={modalData3?.description}
            icon
            ticketCount={ticketCount}
            title={modalData3?.title}
          />
        ) : null;
      case 'API_ODT':
        return <TicketsErrorModal />;
      default:
        return (
          <ModalErrorCard
            closeModal={onClose}
            description={errorDescription}
            errorBlock={errorBlock}
            title={errorMessageTitle}
          />
        );
    }
  })();

  return (
    <Box>
      <div aria-live="assertive" className="sr-only" role="alert">
        {errorDescription}
      </div>
      <ThemeProvider theme={getThemeByName('Rock Center')}>
        {/** a11y: aria-live & role setting for accessbility announcement */}
        <Modal
          id="buy-tickets-modal"
          isOpen={isOpen}
          onClose={onClose}
          onCloseFinished={onCloseFinished}
        >
          {modalMarkup}
        </Modal>
      </ThemeProvider>
    </Box>
  );
};
